"use client";

import { LogIn, MessageSquare, Send } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { io } from "socket.io-client";
import { API_URL, apiFetch } from "../lib/api";

export function SimulatorChatClient({ simulatorSlug }) {
  const [state, setState] = useState({
    loading: true,
    authenticated: false,
    connected: false,
    error: "",
    user: null,
    messages: [],
  });
  const [message, setMessage] = useState("");
  const socketRef = useRef(null);
  const listRef = useRef(null);

  const canSend = state.authenticated && state.connected && message.trim().length > 0;

  useEffect(() => {
    let cancelled = false;

    async function boot() {
      try {
        const me = await apiFetch("/api/v1/auth/me", { cache: "no-store" });
        if (cancelled) return;
        setState((current) => ({ ...current, loading: false, authenticated: true, user: me.user }));
        connectSocket();
      } catch {
        if (cancelled) return;
        setState((current) => ({ ...current, loading: false, authenticated: false, connected: false }));
      }
    }

    function connectSocket() {
      const socket = io(API_URL, {
        withCredentials: true,
        transports: ["websocket", "polling"],
      });

      socketRef.current = socket;

      socket.on("connect", () => {
        setState((current) => ({ ...current, connected: true, error: "" }));
        socket.emit("simulator:join", { simulatorSlug, limit: 50 }, (response) => {
          if (!response?.ok) {
            setState((current) => ({
              ...current,
              connected: false,
              error: response?.error?.message || "No se pudo abrir el chat.",
            }));
            return;
          }
          setState((current) => ({ ...current, messages: response.messages || [] }));
        });
      });

      socket.on("disconnect", () => {
        setState((current) => ({ ...current, connected: false }));
      });

      socket.on("connect_error", () => {
        setState((current) => ({ ...current, connected: false, error: "No se pudo conectar al chat." }));
      });

      socket.on("simulator:message", (payload) => {
        if (payload?.simulator?.slug !== simulatorSlug || !payload?.message?.id) return;
        setState((current) => {
          if (current.messages.some((item) => item.id === payload.message.id)) return current;
          return { ...current, messages: [...current.messages, payload.message].slice(-80) };
        });
      });
    }

    boot();

    return () => {
      cancelled = true;
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, [simulatorSlug]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [state.messages.length]);

  function sendMessage(event) {
    event.preventDefault();
    const text = message.trim();
    if (!canSend || !socketRef.current) return;

    socketRef.current.emit("simulator:message", { simulatorSlug, message: text }, (response) => {
      if (!response?.ok) {
        setState((current) => ({
          ...current,
          error: response?.error?.message || "No se pudo enviar el mensaje.",
        }));
        return;
      }
      setMessage("");
    });
  }

  const statusLabel = useMemo(() => {
    if (state.loading) return "Cargando";
    if (!state.authenticated) return "Solo pilotos";
    return state.connected ? "En línea" : "Reconectando";
  }, [state.loading, state.authenticated, state.connected]);

  return (
    <article className="simulator-side-card simulator-chat-card">
      <header>
        <strong>Chat Discord</strong>
        <span>{statusLabel}</span>
      </header>

      {!state.authenticated && !state.loading ? (
        <div className="simulator-chat-empty">
          <MessageSquare size={22} />
          <p>Inicia sesión como piloto para leer y escribir en este chat.</p>
          <a className="nitro-outline-button" href="/login"><LogIn size={16} /> Iniciar sesión</a>
        </div>
      ) : (
        <div className="simulator-chat-messages" ref={listRef}>
          {state.messages.length ? state.messages.map((item) => (
            <ChatMessage item={item} key={item.id} mine={item.author?.userId === state.user?.id} />
          )) : (
            <div className="simulator-chat-empty simulator-chat-empty--compact">
              <MessageSquare size={22} />
              <p>{state.loading ? "Cargando chat..." : "Aún no hay mensajes en este simulador."}</p>
            </div>
          )}
        </div>
      )}

      {state.error ? <p className="simulator-chat-error">{state.error}</p> : null}

      <form className="simulator-chat-form" onSubmit={sendMessage}>
        <input
          disabled={!state.authenticated || !state.connected}
          maxLength={500}
          onChange={(event) => setMessage(event.target.value)}
          placeholder={state.authenticated ? "Escribe en Discord..." : "Chat solo para pilotos"}
          value={message}
        />
        <button disabled={!canSend} type="submit" aria-label="Enviar mensaje">
          <Send size={16} />
        </button>
      </form>
    </article>
  );
}

function ChatMessage({ item, mine }) {
  return (
    <div className={`simulator-chat-message ${mine ? "is-mine" : ""}`}>
      <strong>{item.author?.displayName || "Piloto Nitro"}</strong>
      <p>{item.message}</p>
      <time dateTime={item.createdAt}>{formatTime(item.createdAt)}</time>
    </div>
  );
}

function formatTime(value) {
  if (!value) return "";
  return new Intl.DateTimeFormat("es-CO", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "America/Bogota",
  }).format(new Date(value));
}
